import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, TextField, Switch, FormControlLabel, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { ChecklistResponse } from "../../types/editChecklist";
import { getChecklistById, putChecklist } from "../../api/editChecklistApi";

type FormValues = {
  descricao_checklist: string;
  fotos_checklist: boolean;
  checklist_valida_comercial: boolean;
  assinatura_checklist: boolean;
  agrupar_checklist: boolean;
  template_checklist?: string | null;
  legenda?: string | null;
};

const EditarChecklist: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const checklistId = Number(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { control, handleSubmit, reset } = useForm<FormValues>({ defaultValues: {} as FormValues });

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token") || "";
        const data: ChecklistResponse = await getChecklistById(token, checklistId);
        reset({
          descricao_checklist: data.descricao_checklist ?? "",
          fotos_checklist: !!data.fotos_checklist,
          checklist_valida_comercial: !!data.checklist_valida_comercial,
          assinatura_checklist: !!data.assinatura_checklist,
          agrupar_checklist: !!data.agrupar_checklist,
          template_checklist: data.template_checklist ?? "",
          legenda: data.legenda ?? "",
        });
      } catch (err) {
        console.error(err);
        toast.error("Erro ao carregar checklist");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [checklistId, reset]);

  const onSubmit = async (form: FormValues) => {
    try {
      const token = localStorage.getItem("token") || "";
      await putChecklist(token, checklistId, form);
      toast.success("Checklist atualizado com sucesso");
      navigate("/checklists");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao atualizar checklist");
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h5" gutterBottom>Editar Checklist #{checklistId}</Typography>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Controller
          name="descricao_checklist"
          control={control}
          rules={{ required: "Descrição é obrigatória" }}
          render={({ field, fieldState }) => (
            <TextField {...field} label="Descrição do Checklist" fullWidth margin="normal" error={!!fieldState.error} helperText={fieldState.error?.message} />
          )}
        />

        <Controller
          name="template_checklist"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Template" fullWidth margin="normal" />
          )}
        />

          <Controller
          name="legenda"
          control={control}
          render={({ field }) => (
            <TextField {...field} label="Legenda" fullWidth margin="normal" />
          )}
        />

        <Controller
          name="fotos_checklist"
          control={control}
          render={({ field }) => (
            <FormControlLabel control={<Switch {...field} checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Fotos no Checklist" />
          )}
        />

        <Controller
          name="assinatura_checklist"
          control={control}
          render={({ field }) => (
            <FormControlLabel control={<Switch {...field} checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Assinatura no Checklist" />
          )}
        />

        <Controller
          name="checklist_valida_comercial"
          control={control}
          render={({ field }) => (
            <FormControlLabel control={<Switch {...field} checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Valida Comercial" />
          )}
        />

        <Controller
          name="agrupar_checklist"
          control={control}
          render={({ field }) => (
            <FormControlLabel control={<Switch {...field} checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Agrupar Checklist" />
          )}
        />

        <Box mt={2} display="flex" gap={2}>
          <Button variant="contained" color="primary" type="submit">Salvar</Button>
          <Button variant="outlined" component={Link} to={`/checklist/${checklistId}/grupos`}>Editar Grupos</Button>
          <Button variant="text" onClick={() => navigate(-1)}>Voltar</Button>
        </Box>
      </form>
    </Box>
  );
};

export default EditarChecklist;
